/**
 * «سودوکو»'s play time — exact, from timestamps, never from counting ticks. Time accrues only between
 * `start()` and `pause()`; the screen pauses it whenever the app is hidden (and for a dialog), so the
 * time a record is made of is time the game was actually on screen.
 *
 * It owns no timer: the screen asks `elapsed()` when it paints — once a second while the game is on
 * screen and unsolved, and never while it is hidden.
 */
export class SudokuClock {
  private base: number;
  private since: number | null = null;

  constructor(
    elapsedMs: number,
    private readonly now: () => number = () => performance.now(),
  ) {
    this.base = Math.max(0, elapsedMs);
  }

  get running(): boolean {
    return this.since !== null;
  }

  start(): void {
    if (this.since === null) this.since = this.now();
  }

  pause(): void {
    if (this.since === null) return;
    this.base += Math.max(0, this.now() - this.since);
    this.since = null;
  }

  elapsed(): number {
    return Math.round(this.base + (this.since === null ? 0 : Math.max(0, this.now() - this.since)));
  }

  /** Take another phone's time (an adopted game): the clock goes on from there. */
  set(elapsedMs: number): void {
    this.base = Math.max(0, elapsedMs);
    if (this.since !== null) this.since = this.now();
  }
}

/** `m:ss` (or `h:mm:ss`) — Latin digits; the screen runs it through `faDigits`. */
export function formatPlayTime(ms: number): string {
  const total = Math.floor(Math.max(0, ms) / 1000);
  const h = Math.floor(total / 3600);
  const m = Math.floor((total % 3600) / 60);
  const s = total % 60;
  const ss = String(s).padStart(2, "0");
  return h > 0 ? `${h}:${String(m).padStart(2, "0")}:${ss}` : `${m}:${ss}`;
}
